import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, Button, Typography, DatePicker, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconTool } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { message } from '../services/toastMessage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { useAntdPickerLocale } from '../hooks/useAntdPickerLocale';
import { takeMachineryOutOfService } from '../services/HeavyDutyMachineryService';
import { IAffectedBooking, IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { TextArea } = Input;
const { Text } = Typography;

interface MachineryOutOfServiceModalProps {
  machineryId: string;
  machineryTitle?: string;
  open: boolean;
  onClose: () => void;
  /** The updated machine plus whatever bookings now run into the window. */
  onParked: (machinery: IHeavyDutyMachinery, affected: IAffectedBooking[]) => void;
}

/**
 * "Esta máquina no está operativa" -- the host's handbrake.
 *
 * One component rather than one per screen, because this gets asked from two
 * completely different places for the same reason: from the machine list,
 * when the host knows it is broken, and right after a rental closes, when
 * the machine has just come back in a state nobody expected. Same decision,
 * same fields, same consequences -- a second copy would have drifted the
 * first time either was touched.
 *
 * The date is optional and it is the field that matters most. A host who
 * puts one gets the machine back on the market by itself; a host who leaves
 * it empty has to remember, and the person who would have to remember is
 * the one lying under the machine on a Friday evening. So it is offered
 * first, phrased as a question rather than a requirement.
 *
 * The reason is required but never shown to a guest -- a customer is told
 * the dates are unavailable, not that the machine is broken. It is there so
 * that a host with three machines parked can tell them apart in a fortnight.
 */
const MachineryOutOfServiceModal: React.FC<MachineryOutOfServiceModalProps> = ({
  machineryId,
  machineryTitle,
  open,
  onClose,
  onParked,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const pickerLocale = useAntdPickerLocale();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Drag from the grab handle only -- the sheet holds a TextArea and a date
  // picker the host taps inside, which a whole-sheet drag listener fights
  // with. Same reasoning as HostCancelBookingModal.
  const dragControls = useDragControls();

  const handleClose = () => {
    setReason('');
    setUntil(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_REASON_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      // End of the chosen day, not its midnight: a host who says "back on
      // Tuesday" means they have it all of Tuesday, not that it frees up
      // the instant Monday ends.
      const result = await takeMachineryOutOfService(
        machineryId,
        reason.trim(),
        until ? until.endOf('day').toISOString() : undefined
      );
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_SUCCESS));
      onParked(result.machinery, result.affectedBookings ?? []);
      handleClose();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const body = (
    <>
      <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_DESCRIPTION)}
      </Text>

      <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_UNTIL_LABEL)}
      </Text>
      <DatePicker
        locale={pickerLocale}
        value={until}
        onChange={setUntil}
        // Today is not a valid answer: a machine back today was never out.
        disabledDate={(current) => current && current < dayjs().endOf('day')}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_UNTIL_PLACEHOLDER)}
        style={{ width: '100%', borderRadius: 999, marginBottom: 4 }}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_UNTIL_HELP)}
      </Text>

      <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_REASON_LABEL)}
      </Text>
      <TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_REASON_PLACEHOLDER)}
        maxLength={500}
        style={{ borderRadius: 16, marginBottom: 6 }}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_REASON_HELP)}
      </Text>
    </>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="out-of-service-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="out-of-service-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: token.colorWarningBg,
                    color: token.colorWarning,
                  }}
                >
                  <IconTool size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ display: 'block', fontSize: 16 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_TITLE)}
                  </Text>
                  {machineryTitle && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {machineryTitle}
                    </Text>
                  )}
                </div>
              </div>
              {body}
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <Button
                  block
                  style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
                  onClick={handleClose}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
                </Button>
                <Button
                  block
                  type="primary"
                  loading={submitting}
                  onClick={handleSubmit}
                  style={{ borderRadius: 999 }}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_CONFIRM)}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_TITLE)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_CONFIRM)}
      confirmLoading={submitting}
    >
      {machineryTitle && (
        <Text strong style={{ display: 'block', marginBottom: 10 }}>
          {machineryTitle}
        </Text>
      )}
      {body}
    </Modal>
  );
};

export default MachineryOutOfServiceModal;
